import { ToolExecution } from "@/app/lib/stores/useCopilotStore";
import { CheckCircleIcon, XCircleIcon, ClockIcon, ExclamationCircleIcon, CogIcon } from "@heroicons/react/24/outline";
import { cn } from "@/app/lib/utils";

interface ToolExecutionStatusProps {
  execution: ToolExecution;
}

export function ToolExecutionStatus({ execution }: ToolExecutionStatusProps) {
  const getStatusIcon = () => {
    switch (execution.status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'failed':
        if (execution.failure_type === 'timeout') {
          return <ClockIcon className="w-4 h-4 text-orange-600" />;
        } else if (execution.failure_type === 'denied') {
          return <XCircleIcon className="w-4 h-4 text-red-600" />;
        } else {
          return <ExclamationCircleIcon className="w-4 h-4 text-red-600" />;
        }
      case 'started':
        return <CogIcon className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (execution.status) {
      case 'completed':
        return 'Tool executed successfully';
      case 'failed':
        if (execution.failure_type === 'timeout') {
          return 'Tool execution timed out';
        } else if (execution.failure_type === 'denied') {
          return 'Tool execution denied';
        } else {
          return 'Tool execution failed';
        }
      case 'started':
        return 'Tool executing...';
      default:
        return '';
    }
  };

  const getBorderColor = () => {
    switch (execution.status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'failed':
        if (execution.failure_type === 'timeout') {
          return 'border-orange-200 bg-orange-50';
        } else {
          return 'border-red-200 bg-red-50';
        }
      case 'started':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  // Don't show tool executions that require confirmation and are still pending
  if (execution.requires_confirmation && execution.status === 'started') {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-start gap-2 p-3 rounded-lg border transition-all duration-200",
        getBorderColor()
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getStatusIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">
            {execution.tool_name}
          </span>
          <span className="text-xs text-gray-500">
            {getStatusText()}
          </span>
        </div>
        {execution.reason && (
          <p className="text-sm text-gray-600 mt-1">
            {execution.reason}
          </p>
        )}
        {execution.result && execution.status === 'completed' && (
          <p className="text-sm text-gray-600 mt-1 truncate">
            {execution.result}
          </p>
        )}
      </div>
    </div>
  );
}